import { fetchResource, postResource } from '@/services/fetchService.js'

class QuizService {
  constructor() {
    this.type = ''
  }

  fetchQuizzes = async () => {
    const quizzes = await fetchResource('quizzes')
    return quizzes
  }

  fetchQuiz = async ({ id, slug }) => {
    const resource = 'quiz'
    const path = id ? `${resource}/${id}` : `${resource}/slug/${slug}`
    const quiz = await fetchResource(path)
    return quiz
  }

  postResults = async (quizResults) => {
    const quizSlug = quizResults.quiz.slug
    const resource = `quiz/${quizSlug}/results`
    const response = await postResource(resource, quizResults)
    return response
  }

  generateResponses(quiz, userResponses) {
    return quiz.questions.map((q, qIndex) => {
      // points per question
      const numberOfCorrectAnswers = q.options.filter((o) => o.correct).length
      const isMultipleChoice = numberOfCorrectAnswers > 1
      const maxScorePerQuestion = 1
      const pointsPerCorrectAnswer = isMultipleChoice
        ? maxScorePerQuestion / numberOfCorrectAnswers
        : maxScorePerQuestion
      const penaltyPerWrongAnswer = isMultipleChoice
        ? -(maxScorePerQuestion / numberOfCorrectAnswers)
        : 0

      // merge quiz answers with user responses
      return {
        question: q.question.query,
        isMultipleChoice,
        maxScore: maxScorePerQuestion,
        options: q.options.map((option, oIndex) => {
          return {
            answer: option.answer,
            correct: option.correct,
            pointsCorrect: option.correct ? pointsPerCorrectAnswer : penaltyPerWrongAnswer,
            // TODO: enable penalties for incorrect answers
            pointsIncorrect: option.correct ? 0 : 0,
            // did the user select this response?
            selected: isMultipleChoice
              ? userResponses[qIndex].includes(oIndex)
              : userResponses[qIndex] === oIndex
          }
        })
      }
    })
  }

  calculateScore(quizResponses) {
    return quizResponses.reduce((runningScore, response) => {
      const questionScore = response.options.reduce((responseScore, option) => {
        // only scores of user selections are included (vs. non-selections)
        const optionScore = option.selected ? option.pointsCorrect : option.pointsIncorrect
        return responseScore + optionScore
      }, 0)
      const adjustedQuestionScore = questionScore < 0 ? 0 : questionScore
      return runningScore + adjustedQuestionScore
    }, 0)
  }

  calculateAggregateScore(quiz, userResponses) {
    let initialScore = {
      numericScore: 0,
      percentageScore: 0,
      quizTotalCorrectAnswers: 0,
      userTotalCorrectAnswers: 0,
      userTotalIncorrectAnswers: 0,
      userTotalPoints: 0
    }

    /* assumptions:
     * 1)single choice: maxScore = 1, pointsCorrect = 1, pointsIncorrect = 0
     * 2)mutiple choice: maxScore = 1, pointsCorrect = 1, pointsIncorrect = -1
     * 3)minScore >= 0
     */

    // FIXME: add pointsCorrect / subtract pointsIncorrect to / from score
    // FIXME: normalize to maxScore of question

    return userResponses.reduce((score, response, i) => {
      // response is either an array of indexes (multiple-choice) or a single index (single-choice)
      if (Array.isArray(response)) {
        // count the number of correct responses for this question
        const questionCorrectAnswers = quiz.questions[i].options.filter(
          (option) => option.correct
        ).length
        score.quizTotalCorrectAnswers += questionCorrectAnswers

        // count the number of selected *correct* answers for this question
        const userCorrectAnswers = response.filter(
          (selection) => quiz.questions[i].options[selection].correct
        ).length
        score.userTotalCorrectAnswers += userCorrectAnswers

        // count the number of selected *incorrect* answers for this question
        const userIncorrectAnswers = response.filter(
          (selection) => quiz.questions[i].options[selection].correct === false
        ).length
        score.userTotalIncorrectAnswers += userIncorrectAnswers

        // accumulate the score (negative score is a 0)
        const actualPoints = userCorrectAnswers - userIncorrectAnswers
        const points = actualPoints >= 0 ? actualPoints : 0
        score.userTotalPoints += points
        score.numericScore += points / questionCorrectAnswers
      } else {
        // only a single correct answer exists
        score.quizTotalCorrectAnswers += 1
        if (response != null && quiz.questions[i].options[response].correct) {
          score.userTotalCorrectAnswers += 1
          score.userTotalPoints += 1
          score.numericScore += 1
        } else {
          score.userTotalIncorrectAnswers += 1
          score.numericScore += 0
        }
      }

      score.percentageScore = Math.round(
        (score.userTotalPoints * 100) / score.quizTotalCorrectAnswers
      )

      return score
    }, initialScore)
  }
}

export default QuizService
export const quizService = new QuizService()
